import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useScrollReveal } from "../lib/hooks";
import { SectionBadge } from "./Shared";
import { FONT_HEAD, FONT_BODY, NAVY, GOLD } from "../lib/constants";

export const FAQS = [
  {
    q: "How much does house cleaning cost in Naples, FL?",
    a: "Most professional house cleanings in Naples, FL range from $120 to $300+ depending on your home's size, condition, and how often you schedule service. Recurring weekly or bi-weekly cleaning costs less per visit than a one-time deep clean. Get an exact number with our free online estimate or call (239) 244-1934.",
    link: { to: "/estimate", label: "Get your free 60-second estimate" },
  },
  {
    q: "Do you offer Airbnb turnover cleaning?",
    a: "Yes — Airbnb and vacation rental turnover cleaning is one of our specialties in Naples and Collier County. We handle linens, restocking, staging, and guest-ready inspections between check-outs so your listing always earns 5-star cleanliness reviews. Same-day turnovers are available.",
    link: { to: "/services", label: "See our Airbnb cleaning service" },
  },
  {
    q: "Are you licensed and insured?",
    a: "Absolutely. Cindyrella Cleaning LLC is a fully licensed and insured cleaning company in the state of Florida. That means your home or business is protected every time our professional cleaning team is on site — no exceptions.",
  },
  {
    q: "What areas do you serve?",
    a: "We provide residential and commercial cleaning services throughout Naples, FL and all of Collier County — including North Naples, East Naples, Golden Gate, Marco Island, and Bonita Springs. If you're nearby, reach out — we may still be able to help.",
  },
  {
    q: "How do I get a free cleaning estimate?",
    a: "Getting a free cleaning estimate is easy: use our online estimate calculator for an instant price in 60 seconds, call or text us at (239) 244-1934, or fill out the quote form on our contact page. We respond to every request within 24 hours.",
    link: { to: "/contact", label: "Request your free quote" },
  },
];

const FaqItem = ({ q, a, link, idx }) => (
  <details
    data-testid={`faq-item-${idx}`}
    className="group bg-white"
    style={{ border: "1px solid rgba(27,58,107,0.1)" }}
  >
    <summary
      className="flex items-center justify-between gap-4 cursor-pointer list-none px-6 py-5 text-base font-bold select-none"
      style={{ fontFamily: FONT_HEAD, color: NAVY }}
    >
      {q}
      <ChevronDown size={18} className="flex-shrink-0 transition-transform duration-300 group-open:rotate-180" style={{ color: GOLD }} />
    </summary>
    <div className="px-6 pb-6">
      <p className="text-sm leading-relaxed" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
        {a}
      </p>
      {link && (
        <Link
          to={link.to}
          data-testid={`faq-link-${idx}`}
          className="inline-block mt-3 text-sm font-bold hover:underline"
          style={{ color: GOLD, fontFamily: FONT_BODY }}
        >
          {link.label} →
        </Link>
      )}
    </div>
  </details>
);

export const FaqSection = () => {
  const ref = useScrollReveal();
  return (
    <section id="faq" data-testid="faq-section" ref={ref} className="py-20 md:py-28 section-hidden" style={{ background: "#F9FAFB" }}>
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <SectionBadge>FAQ</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
            Frequently Asked Questions
          </h2>
          <p className="mt-4 text-base max-w-xl mx-auto" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
            Everything Naples homeowners and businesses ask us before booking their first cleaning.
          </p>
        </div>
        <div className="space-y-3">
          {FAQS.map((faq, idx) => (
            <FaqItem key={faq.q} {...faq} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};
